import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { Bell, ChevronsUpDown, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { PublishButton } from "@/components/custom/publish-button";
import { ThemeToggle } from "@/components/custom/theme-toggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarTrigger } from "@/components/ui/sidebar";

type NavbarProps = {
  actions?: ReactNode;
};

export function Navbar({ actions }: NavbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    const isAdmin = user?.type === "admin";
    logout();
    navigate(isAdmin ? "/admin/signin" : "/signin");
  };

  const getInitials = (email?: string) => {
    if (!email) return "U";
    return email.slice(0, 2).toUpperCase();
  };

  const breadcrumbItems = getBreadcrumbItems(location.pathname);
  const tenantName = user?.type === "tenant" ? user.tenant?.name : null;
  const userTypeLabel = user?.type?.charAt(0).toUpperCase() + (user?.type?.slice(1) || "") || "User";

  return (
    <nav className="sticky top-0 z-40 flex h-16 items-center justify-between gap-4 border-b border-sidebar-border bg-sidebar/95 px-4 backdrop-blur-sm">
      <div className="flex min-w-0 items-center gap-3">
        <SidebarTrigger />
        <Breadcrumb className="hidden md:block">
          <BreadcrumbList>
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              return (
                <BreadcrumbItem key={item.href ?? item.label}>
                  <BreadcrumbPage className="text-muted-foreground">{item.label}</BreadcrumbPage>
                  {!isLast ? <BreadcrumbSeparator /> : null}
                </BreadcrumbItem>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="flex items-center gap-2">
        {actions}
        {user?.type === "tenant" && user.tenant?.slug ? (
          <PublishButton tenantSlug={user.tenant.slug} />
        ) : null}
        <ThemeToggle className="text-muted-foreground hover:text-foreground" />
        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
          <Bell className="size-5" aria-hidden="true" />
          <span className="sr-only">Open notifications</span>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-3 rounded-lg px-2 py-1 transition-colors hover:bg-muted/70 focus:outline-none data-[state=open]:bg-muted data-[state=open]:text-sidebar-accent-foreground">
            <Avatar className="size-9 rounded-full border border-primary/25 bg-primary/10 shadow-sm">
              <AvatarFallback className="rounded-full font-semibold">
                {getInitials(user?.email)}
              </AvatarFallback>
            </Avatar>
            <div className="hidden min-w-0 text-left text-sm leading-[1.2] md:flex md:flex-col md:justify-center">
              <span className="max-w-[180px] truncate font-medium text-foreground">
                {user?.email || <span className="italic text-muted-foreground">No email</span>}
              </span>
              <span className="max-w-[110px] truncate text-xs font-semibold text-primary/80">
                {tenantName ? `${userTypeLabel}: ${tenantName}` : userTypeLabel}
              </span>
            </div>
            <ChevronsUpDown className="ml-1 size-4 text-muted-foreground" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-xl border-muted/40 p-1.5 shadow-lg"
            side="bottom"
            align="end"
            sideOffset={8}
          >
            <div className="mb-1 border-b border-muted px-2 py-2">
              <div className="truncate text-sm font-semibold text-foreground">{user?.email}</div>
              <div className="truncate text-xs text-muted-foreground">
                {tenantName ? `${userTypeLabel}: ${tenantName}` : userTypeLabel}
              </div>
            </div>
            <DropdownMenuItem className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors hover:bg-muted">
              <Settings className="size-4 text-muted-foreground" />
              <span className="font-medium">Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleLogout}
              className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="size-4 text-destructive" />
              <span className="font-medium">Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}

type BreadcrumbNode = {
  label: string;
  href?: string;
};

function getBreadcrumbItems(pathname: string): BreadcrumbNode[] {
  if (pathname.startsWith("/admin/dashboard")) {
    const adminItems = buildPathBreadcrumbs(pathname, "/admin/dashboard", "Console");
    return adminItems.length > 0 ? adminItems : [{ label: "Console" }];
  }

  if (pathname.startsWith("/tenant/dashboard")) {
    const tenantItems = buildPathBreadcrumbs(pathname, "/tenant/dashboard", "Workspace");
    return tenantItems.length > 0 ? tenantItems : [{ label: "Workspace" }];
  }

  return [{ label: "Dashboard" }];
}

function buildPathBreadcrumbs(pathname: string, basePath: string, rootLabel: string): BreadcrumbNode[] {
  const segments = pathname
    .replace(basePath, "")
    .split("/")
    .filter(Boolean)
    .filter((segment) => !/^\d+$/.test(segment));

  if (segments.length === 0) {
    return [{ label: rootLabel }];
  }

  const nodes: BreadcrumbNode[] = [{ label: rootLabel, href: basePath }];

  segments.forEach((segment, index) => {
    const currentSegments = segments.slice(0, index + 1);
    const isLast = index === segments.length - 1;
    nodes.push({
      label: formatSegmentLabel(segment),
      href: isLast ? undefined : `${basePath}/${currentSegments.join("/")}`,
    });
  });

  return nodes;
}

function formatSegmentLabel(segment: string) {
  return segment
    .replaceAll("[", "")
    .replaceAll("]", "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
